import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import fleetConfigService, { DEFAULT_READY_SOC } from "src/services/fleetConfigService";
import { alertSlice } from "src/store/alert/alert.slice";

/**
 * Company → Settings → Fleet (super admin): fleet mode (the dashboard also shows
 * Energy Cost next to Revenue), the SoC at which a truck counts as Ready, and the
 * electricity tariff (₹/kWh) of each of the company's charging sites. Teams
 * inherit the company's fleet mode and ready SoC.
 */
const CompanyFleetSettings = ({ companyId }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [fleetMode, setFleetMode] = useState(false);
  const [readySoc, setReadySoc] = useState("");
  const [savedReadySoc, setSavedReadySoc] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const [sites, setSites] = useState([]);
  const [tariffs, setTariffs] = useState({}); // zoneId -> input text
  const [savingSite, setSavingSite] = useState(null);

  const alert = (message, severity = "success") =>
    dispatch(alertSlice.actions.setAlert({ open: true, message, severity }));
  const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

  useEffect(() => {
    let active = true;
    if (!companyId) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      setLoading(true);
      const [config, siteList] = await Promise.all([
        fleetConfigService.getCompanyFleetConfig(companyId),
        fleetConfigService.getCompanySiteTariffs(companyId),
      ]);
      if (!active) return;
      const ready = config.readySocPct == null ? "" : String(config.readySocPct);
      setFleetMode(config.fleetModeEnabled);
      setReadySoc(ready);
      setSavedReadySoc(ready);
      setSites(siteList);
      setTariffs(
        Object.fromEntries(
          siteList.map((s) => [s.zoneId, s.electricityTariff == null ? "" : String(s.electricityTariff)])
        )
      );
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [companyId]);

  const readySocValue = readySoc === "" ? null : Number(readySoc);
  const readySocValid =
    readySocValue === null || (Number.isInteger(readySocValue) && readySocValue >= 1 && readySocValue <= 100);

  // Dashboards cache these per team; drop them so changes show at once.
  const refreshDashboards = () => queryClient.invalidateQueries(["fleetConfig"]);

  const saveConfig = async (next) => {
    setSavingConfig(true);
    try {
      const result = await fleetConfigService.updateCompanyFleetConfig(companyId, next);
      const ready = result.readySocPct == null ? "" : String(result.readySocPct);
      setFleetMode(result.fleetModeEnabled);
      setReadySoc(ready);
      setSavedReadySoc(ready);
      refreshDashboards();
      alert("Fleet settings saved");
      return true;
    } catch (error) {
      alert(errorMessage(error, "Failed to save fleet settings"), "error");
      return false;
    } finally {
      setSavingConfig(false);
    }
  };

  const handleFleetMode = async (event) => {
    const next = event.target.checked;
    setFleetMode(next); // optimistic
    // Keep the saved ready SoC, not an unsaved edit in the box.
    const ok = await saveConfig({
      fleetModeEnabled: next,
      readySocPct: savedReadySoc === "" ? null : Number(savedReadySoc),
    });
    if (!ok) setFleetMode(!next);
  };

  const saveTariff = async (zoneId) => {
    const raw = tariffs[zoneId] ?? "";
    const value = raw === "" ? null : Number(raw);
    if (value !== null && (!Number.isFinite(value) || value < 0 || value > 1000)) {
      alert("Tariff must be between 0 and 1000 ₹/kWh", "error");
      return;
    }
    setSavingSite(zoneId);
    try {
      await fleetConfigService.updateCompanySiteTariff(companyId, zoneId, value);
      setSites((prev) => prev.map((s) => (s.zoneId === zoneId ? { ...s, electricityTariff: value } : s)));
      alert("Tariff saved");
    } catch (error) {
      alert(errorMessage(error, "Failed to save tariff"), "error");
    } finally {
      setSavingSite(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="subtitle2">Fleet mode</Typography>
              <Typography variant="body2" color="text.secondary">
                For fleets: the dashboard also shows Energy Cost (energy × each site&apos;s electricity tariff below)
                next to Revenue. Applies to this company and all of its teams.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={<Switch checked={fleetMode} onChange={handleFleetMode} disabled={savingConfig} />}
              label={fleetMode ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle2">Ready at SoC</Typography>
              <Typography variant="body2" color="text.secondary">
                A truck counts as Ready on the dashboard&apos;s Readiness view when every battery is at or above this
                level. Leave empty to use the parent company&apos;s value, or {DEFAULT_READY_SOC}%.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexShrink: 0 }}>
              <TextField
                size="small"
                type="number"
                value={readySoc}
                placeholder={String(DEFAULT_READY_SOC)}
                onChange={(e) => setReadySoc(e.target.value)}
                error={!readySocValid}
                helperText={readySocValid ? " " : "1–100"}
                inputProps={{ min: 1, max: 100, step: 1, "aria-label": "Ready at SoC percent" }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ width: 120 }}
              />
              <Button
                variant="contained"
                disabled={savingConfig || !readySocValid || readySoc === savedReadySoc}
                onClick={() => saveConfig({ fleetModeEnabled: fleetMode, readySocPct: readySocValue })}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2">Electricity tariff by charging site</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            What you pay for electricity at each site (depot), in ₹ per kWh. Used for Energy Cost on the dashboard;
            energy at a site with no tariff is shown as not costed.
          </Typography>
          {sites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              This company has no charging sites yet. Create them under Charging Site.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Site</TableCell>
                  <TableCell>Tariff</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((site) => {
                  const text = tariffs[site.zoneId] ?? "";
                  const saved = site.electricityTariff == null ? "" : String(site.electricityTariff);
                  return (
                    <TableRow key={site.zoneId}>
                      <TableCell>{site.zoneName}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={text}
                          placeholder="Not set"
                          onChange={(e) => setTariffs((prev) => ({ ...prev, [site.zoneId]: e.target.value }))}
                          inputProps={{ min: 0, max: 1000, step: 0.01, "aria-label": `Tariff for ${site.zoneName}` }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/kWh</InputAdornment>,
                          }}
                          sx={{ width: 180 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={savingSite === site.zoneId || text === saved}
                          onClick={() => saveTariff(site.zoneId)}
                        >
                          Save
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

CompanyFleetSettings.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CompanyFleetSettings;
